import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, LifeBuoy, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

/**
 * Client-side 404 view. Rendered by <Route path="*"> in App.js so any
 * URL that does not match a real React route lands here.
 *
 * WHY THIS EXISTS
 * ---------------
 * There are three independent layers that keep search engines from
 * indexing junk URLs on postmoro.com; this component is the layer
 * that ALWAYS works regardless of where the site is hosted:
 *
 *   1. Preview / `yarn start` → craco devServer middleware
 *      (frontend/plugins/spa-404/spa-404-middleware.js) rewrites the
 *      SPA fallback's HTTP 200 → 404.
 *   2. Static production hosting via `serve build` (Vercel's serve),
 *      Netlify, Cloudflare Pages, GitHub Pages → build/404.html
 *      generated by
 *      frontend/plugins/spa-404/generate-404-html-plugin.js AND
 *      public/serve.json restricts rewrites to the known route
 *      allow-list, so unknown paths naturally fall through to
 *      404.html and are served with HTTP 404.
 *   3. This component → whatever the wire status ends up being
 *      (even in the worst case of `serve -s build` which forces
 *      HTTP 200), React mounts on the unknown URL and this page
 *      injects `<meta name="robots" content="noindex, nofollow">`.
 *      Google honours noindex regardless of status code, so the URL
 *      is dropped from the index.
 *
 * The visible UI is also a real 404 experience — a small, calm
 * page with a home link, a support link, and a "back" button —
 * instead of the previous behavior which silently redirected to /
 * (that redirect masked broken links and made debugging harder).
 */
export default function NotFoundPage() {
  return (
    <>
      <SEO
        title="Page not found | Postmoro"
        description="The page you were looking for doesn't exist on Postmoro. Return home or contact support if you followed a link that should work."
        path="/"
        noindex
      />
      <main
        className="min-h-screen flex items-center justify-center bg-[color:var(--bg-0)] text-[color:var(--text-1)] px-4 sm:px-6"
        data-testid="not-found-page"
      >
        <div className="max-w-xl w-full text-center">
          <p
            className="font-mono text-xs uppercase tracking-[0.24em] text-[color:var(--text-3)]"
            data-testid="not-found-code"
          >
            404 · page not found
          </p>
          <h1
            className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight"
            data-testid="not-found-title"
          >
            We couldn&apos;t find that page
          </h1>
          <p
            className="mt-4 text-[color:var(--text-2)] text-base leading-relaxed"
            data-testid="not-found-description"
          >
            The link may be broken, the page may have moved, or you may have
            typed the address slightly wrong. Nothing on your account has
            changed.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Link to="/" data-testid="not-found-home-link">
              <Button
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold min-w-[10rem]"
                data-testid="not-found-home-button"
              >
                <Home className="h-4 w-4 mr-2" aria-hidden />
                Go home
              </Button>
            </Link>
            <Button
              variant="ghost"
              onClick={() => {
                if (typeof window !== "undefined" && window.history.length > 1) {
                  window.history.back();
                }
              }}
              className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)] min-w-[10rem]"
              data-testid="not-found-back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden />
              Go back
            </Button>
            <a
              href="mailto:support@postmoro.com"
              className="inline-flex items-center gap-2 text-sm text-[color:var(--text-3)] hover:text-[color:var(--text-1)] transition-colors"
              data-testid="not-found-support-link"
            >
              <LifeBuoy className="h-4 w-4" aria-hidden />
              Contact support
            </a>
          </div>
        </div>
      </main>
    </>
  );
}
